import React, {useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import {
    setSelectedLoanersRentals,
    setSelectedLoanersRentalsTab,
    setSelectedLoanersRentalsView,
    setCachedLoanersRentals,
} from 'reducers/orders';
import axios from 'axios';
import styled from 'styled-components';
import withViewToggle from 'components/OrdersHOC';
import PropTypes from 'prop-types';
import {GridKeys} from 'utils/gridKeys/loanersAndRentalsGridKeys';
import MultiView, {ViewType, GridNames} from 'components/MultiView';

const Container = styled.div`
    width: 100%;
    top: 0;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
`;

const LegendSpan = styled.span`
    font-size: 14px;
    line-height: 18px;
    font-weight: '100';
    margin-left: 5px;
    padding-right: 20px;
`;

const LegendWrapper = styled.div`
    margin-top: 15px;
    position: relative;
`;

const UrgentLegend = styled.div`
    background-color: #fbcbcb;
    width: 15px;
    height: 15px;
    margin-left: 15px;
    margin-top: 2px;
    margin-right: 5px;
    display: inline-block;
    margin-bottom: -2px;
`;

const LegendTitle = styled.span` 
    font-weight: 600;
    font-size: 14px;
`;

const PendingDueDate = styled.span`
    background-color: #ffca82;
    width: 15px;
    height: 15px;
    margin-left: 5px;
    margin-top: 2px;
    margin-right: 5px;
    display: inline-block;
    margin-bottom: -2px;
`;

const LoanersAndRentals = (props) => {
    const {setViewRef, isMobile, isPhone, view, setView} = props;
    const gridName = GridNames.loanersAndRentals.id;
    const history = useHistory();
    const [columns, setColumns] = useState([]);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedLoanersRentalsOrder, setGridSelectedLoanersRentalsOrder] = useState(null);
    const cachedLoanersRentalsOrders = useSelector(state => state.orders.cachedLoanersRentals);
    const cachedView = useSelector(state => state.orders.selectedLoanersRentalsView);
    const dispatch = useDispatch();

    if (selectedLoanersRentalsOrder != null) {
        handleSelectedLoanersRentalsOrder();
    }

    useEffect(() => {
        getCachedData();
    }, []);

    const getData = async () => {
        setLoading(true);
        getGridPreferences();
        const response = await axios.get(`loaner`);
        setData(response.data);
        dispatch(setCachedLoanersRentals(response.data));
        setLoading(false);
    }

    const setColumnList = (columnList) => {
        setColumns(columnList);
    }

    const getCachedData = async () => {
        if (cachedLoanersRentalsOrders.length === 0) {
            getData();
        } else {
            getGridPreferences();
            setView(cachedView);
            setViewRef(cachedView);
            setData(cachedLoanersRentalsOrders);
            setLoading(false);
        }
    }

    const handleSelect = (loanersRental) => {
        let currentLoanersRentalsOrder = {...selectedLoanersRentalsOrder};
        currentLoanersRentalsOrder = loanersRental;
        setGridSelectedLoanersRentalsOrder(currentLoanersRentalsOrder);
    }

    function getBreadcrumbText(status) {
        return GridKeys.find(x => x.value.toLowerCase() === status.toLowerCase()).tabDisplay;
    }

    function handleSelectedLoanersRentalsOrder() {
        if (window.location.search != null)
            dispatch(setSelectedLoanersRentalsTab(window.location.search.split('=')[1]));

        const selectedLoanersRentals = selectedLoanersRentalsOrder.data ?
            selectedLoanersRentalsOrder.data : selectedLoanersRentalsOrder;

        dispatch(setSelectedLoanersRentals(selectedLoanersRentals, {
            firstBreadcrumb: 'Loaners & Rentals',
            secondBreadcrumb: 'Loaner',
            returnText: getBreadcrumbText(selectedLoanersRentals.loanerStatus.toUpperCase()),
        }));
        
        const redirectUrl = selectedLoanersRentals.displayPo ? `/loaners-rentals-details/po/${selectedLoanersRentals.displayPo}`
            : `/loaners-rentals-details/${selectedLoanersRentals.orderNumber}`;

        history.push(redirectUrl);
    }

    const getGridPreferences = async () => {
        const response = await axios.get(`setting/grid/${gridName}`);

        setColumns(response.data.columns);
        dispatch(setSelectedLoanersRentalsView(response.data.view));

        if (!response.data.view || window.innerWidth <= 1149) {
            const viewType = ViewType.Card;
            setView(viewType);
            setViewRef(viewType);
        } else {
            setView(response.data.view);
            setViewRef(response.data.view);
        }
    }

    const renderLegend = () => {
        return (
            <LegendWrapper>
                <LegendTitle>Legend:</LegendTitle>
                <UrgentLegend />
                <LegendSpan>Urgent</LegendSpan>
                <PendingDueDate />
                <LegendSpan>Due Within 5 Days or Less</LegendSpan>
            </LegendWrapper>
        );
    }

    const renderRow = (rowData) => {
        const fiveDaysInFuture = moment().startOf('day').add(5, 'days');

        const isUrgent = rowData.urgencyDescription === 'CHD' || rowData.urgencyDescription === 'STAT';
        const dueShortly = rowData.rgaDueDate <= fiveDaysInFuture;

        return {
            gridhighlightedRow: isUrgent,
            dueShortly,
        };
    }

    const saveGridPreferences = (grid) => {
        dispatch(setSelectedLoanersRentalsView(grid.view));
        setView(grid.view);
        setViewRef(grid.view);
        setColumns(grid.columns);
    }

    return (
        <Container>
            <MultiView
                gridName={gridName}
                label="Loaners"
                view={view}
                selectionMode="single"
                data={data}
                onRenderLegend={renderLegend}
                onRenderRow={renderRow}
                onSaveGridPreferences={saveGridPreferences}
                loadingData={loading}
                setLoadingData={setLoading}
                gridKeys={GridKeys}
                columns={columns}
                setColumnList={setColumnList}
                onRefresh={getData}
                onSelect={handleSelect}
                isMobile={isMobile}
                isPhone={isPhone} />
        </Container>
    );
};

export default withViewToggle(LoanersAndRentals);

LoanersAndRentals.propTypes = {
    setViewRef: PropTypes.func.isRequired,
    isMobile: PropTypes.bool.isRequired,
    isPhone: PropTypes.bool.isRequired,
    view: PropTypes.string,
    setView: PropTypes.func.isRequired,
};
